// Knowledge Base guide — the "Audio" left-rail tab (legacy internal name; its
// visible label is "Knowledge Base" everywhere in the UI).
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const knowledgeBaseGuide = {
  id: "knowledge-base",
  icon: "library_books",
  keywords: [
    "knowledge base",
    "bilgi bankası",
    "summary",
    "özet",
    "mindmap",
    "mind map",
    "zihin haritası",
    "narration",
    "anlatım",
    "seslendirme",
    "narration texts",
    "anlatım metinleri",
    "outline",
    "taslak",
    "document titles",
    "doküman başlıkları",
    "document",
    "belge",
    "doküman",
    "scene sounds",
    "sahne sesleri",
    "copy all",
    "tümünü kopyala",
    "add audio",
    "ses ekle",
    "base de conocimientos",
    "resumen",
    "mapa mental",
    "narración",
    "textos de narración",
    "sonidos de escena",
    "títulos del documento",
    "copiar todo",
  ],
  content: {
    en: {
      feature: "Knowledge Base",
      title: "Knowledge Base",
      sections: [
        {
          id: "overview",
          title: "What the Knowledge Base holds",
          videoUrl: null,
          text:
            "Click **Knowledge Base** in the top bar of the editor to open a panel on the left with what's been generated for this training so far: each scene's narration audio, the summary, the document outline, the mindmap, and the narration texts, all in one place.\n\n" +
            "Pick an entry in the list (**Scene Sounds**, **Summary**, **Document Titles**, **Mindmap**, **Narration Texts**) to open it beside the list. Use it to review what already exists before asking the AI for something new, or to grab text you need elsewhere.",
        },
        {
          id: "panels",
          title: "The panels",
          videoUrl: null,
          text:
            "- **Scene Sounds**: every scene's saved narration audio, grouped by scene. Play a clip, jump to its scene, or edit it.\n" +
            "- **Summary**: the AI-generated summary of the whole training.\n" +
            "- **Document Titles**: the training's outline. It shows the document information plus each section's title, number of scenes, and key points.\n" +
            "- **Mindmap**: click **Open mindmap** to view a visual map of the training's key concepts in a new tab. A mindmap is only produced for courses built from documents.\n" +
            "- **Narration Texts**: every scene's narration script gathered in one list, so you can review or copy them all at once.",
        },
        {
          id: "why",
          title: "Why it matters",
          videoUrl: null,
          text:
            "A scene's saved narration is also the context the AI tools work from for that scene. For example, image generation starts from the scene's narration text. Keeping narration accurate pays off in everything you generate afterwards.\n\n" +
            "To change what a scene actually says out loud, use the clip's **Edit** button in **Scene Sounds**. It jumps to that scene and reopens narration generation there, so the voiced script always matches the text.",
        },
        {
          id: "scene-sound-actions",
          title: "Working with scene narration",
          videoUrl: null,
          text:
            "In **Scene Sounds**, expand a scene to see its narration clips. Long narration text can be expanded with **Show text**. Each clip has these buttons:\n\n" +
            "- **Play**: listen to the clip right in the panel.\n" +
            "- **Video**: opens **Generate Video** with that narration already filled in. A badge counts video jobs that are still running.\n" +
            "- **View scene**: jumps to the scene on the canvas.\n" +
            "- **Edit**: opens the sound window on **AI Narration Voice** for that scene so you can regenerate the narration.\n" +
            "- **Delete**: removes the clip from the scene.\n\n" +
            "To give a scene new narration, use **Add audio** under that scene.",
        },
        {
          id: "copy-text",
          title: "Copying text out",
          videoUrl: null,
          text:
            "**Summary**, **Document Titles**, and **Narration Texts** each have a **Copy All** button at the top that puts the whole panel on your clipboard. **Narration Texts** copies each scene's title followed by its narration.\n\n" +
            "This is handy for reviewing the script outside the editor, sharing it for approval, or reusing it in another document.",
        },
      ],
    },
    tr: {
      feature: "Bilgi Bankası",
      title: "Bilgi Bankası",
      sections: [
        {
          id: "overview",
          title: "Bilgi Bankasında neler var",
          videoUrl: null,
          text:
            "Editörün üst çubuğundaki **Bilgi Bankası** düğmesine tıklayın. Solda, bu eğitim için şimdiye kadar üretilen her şeyin yer aldığı bir panel açılır: her sahnenin anlatım sesi, özet, doküman taslağı, zihin haritası ve anlatım metinleri.\n\n" +
            "Listeden bir başlık seçin (**Sahne Sesleri**, **Özet**, **Doküman Başlıkları**, **Zihin Haritası**, **Anlatım Metinleri**). Seçtiğiniz başlık listenin yanında açılır. Yapay zekâdan yeni bir şey istemeden önce zaten üretilmiş olanı gözden geçirmek ya da başka bir yerde kullanacağınız metni almak için kullanın.",
        },
        {
          id: "panels",
          title: "Paneller",
          videoUrl: null,
          text:
            "- **Sahne Sesleri**: her sahnenin kayıtlı anlatım sesi, sahneye göre gruplanmış. Bir klibi oynatın, sahnesine gidin veya düzenleyin.\n" +
            "- **Özet**: eğitimin tamamının yapay zekâ tarafından oluşturulan özeti.\n" +
            "- **Doküman Başlıkları**: eğitimin taslağı. Doküman bilgisini ve her bölümün başlığını, sahne sayısını ve önemli noktalarını gösterir.\n" +
            "- **Zihin Haritası**: eğitimin temel kavramlarının görsel haritasını yeni sekmede görmek için **Zihin haritasını aç**'a tıklayın. Zihin haritası yalnızca dokümandan oluşturulan kurslarda üretilir.\n" +
            "- **Anlatım Metinleri**: her sahnenin anlatım metni tek bir listede. Hepsini birden gözden geçirebilir veya kopyalayabilirsiniz.",
        },
        {
          id: "why",
          title: "Neden önemli",
          videoUrl: null,
          text:
            "Bir sahnenin kayıtlı anlatımı, yapay zekâ araçlarının o sahne için yola çıktığı bağlamdır. Örneğin görsel üretimi, sahnenin anlatım metninden başlar. Anlatımı doğru tutmak, sonradan ürettiğiniz her şeye yansır.\n\n" +
            "Bir sahnede gerçekte seslendirilen metni değiştirmek için **Sahne Sesleri** içinde klibin **Düzenle** düğmesini kullanın. Sizi ilgili sahneye götürür ve anlatım oluşturmayı orada yeniden açar. Böylece seslendirilen metin her zaman yazıyla eşleşir.",
        },
        {
          id: "scene-sound-actions",
          title: "Sahne anlatımıyla çalışma",
          videoUrl: null,
          text:
            "**Sahne Sesleri** içinde bir sahneyi genişlettiğinizde anlatım kliplerini görürsünüz. Uzun anlatım metnini **Metni göster** ile açabilirsiniz. Her klipte şu düğmeler bulunur:\n\n" +
            "- **Oynat**: klibi doğrudan panelde dinleyin.\n" +
            "- **Video**: o anlatım hazır doldurulmuş olarak **Video Oluştur** penceresini açar. Rozet, hâlâ süren video işlerini sayar.\n" +
            "- **Sahneyi gör**: tuvalde o sahneye gider.\n" +
            "- **Düzenle**: anlatımı yeniden üretebilmeniz için ses penceresini o sahne adına **AI Anlatım Sesi** sekmesinde açar.\n" +
            "- **Sil**: klibi sahneden kaldırır.\n\n" +
            "Bir sahneye yeni anlatım eklemek için o sahnenin altındaki **Ses ekle** düğmesini kullanın.",
        },
        {
          id: "copy-text",
          title: "Metni dışarı kopyalama",
          videoUrl: null,
          text:
            "**Özet**, **Doküman Başlıkları** ve **Anlatım Metinleri** panellerinin üstünde, panelin tamamını panoya kopyalayan bir **Tümünü Kopyala** düğmesi vardır. **Anlatım Metinleri**, her sahnenin başlığını ve ardından anlatımını kopyalar.\n\n" +
            "Senaryoyu editör dışında gözden geçirmek, onaya göndermek ya da başka bir belgede kullanmak için işe yarar.",
        },
      ],
    },
    es: {
      feature: "Base de conocimiento",
      title: "Base de Conocimientos",
      sections: [
        {
          id: "overview",
          title: "Qué contiene la Base de Conocimientos",
          videoUrl: null,
          text:
            "Haz clic en **Base de Conocimientos** en la barra superior del editor para abrir, a la izquierda, un panel con todo lo generado hasta ahora para esta formación: el audio de narración de cada escena, el resumen, el esquema del documento, el mapa mental y los textos de narración, todo en un mismo lugar.\n\n" +
            "Elige una entrada de la lista (**Sonidos de Escena**, **Resumen**, **Títulos del Documento**, **Mapa Mental**, **Textos de Narración**) para abrirla junto a la lista. Úsalo para revisar lo que ya existe antes de pedirle algo nuevo a la IA, o para copiar el texto que necesites en otro sitio.",
        },
        {
          id: "panels",
          title: "Los paneles",
          videoUrl: null,
          text:
            "- **Sonidos de Escena**: el audio de narración guardado de cada escena, agrupado por escena. Reproduce un clip, salta a su escena o edítalo.\n" +
            "- **Resumen**: el resumen de toda la formación generado por IA.\n" +
            "- **Títulos del Documento**: el esquema de la formación. Muestra la información del documento y, para cada sección, su título, el número de escenas y los puntos clave.\n" +
            "- **Mapa Mental**: haz clic en **Abrir mapa mental** para ver en una pestaña nueva un mapa visual de los conceptos clave de la formación. El mapa mental solo se genera para los cursos creados a partir de documentos.\n" +
            "- **Textos de Narración**: el guion de narración de cada escena reunido en una sola lista, para que puedas revisarlos o copiarlos todos a la vez.",
        },
        {
          id: "why",
          title: "Por qué es importante",
          videoUrl: null,
          text:
            "La narración guardada de una escena es también el contexto del que parten las herramientas de IA para esa escena. Por ejemplo, la generación de imágenes parte del texto de narración de la escena. Mantener la narración correcta se nota en todo lo que generes después.\n\n" +
            "Para cambiar lo que una escena dice realmente en voz alta, usa el botón **Editar** del clip en **Sonidos de Escena**. Te lleva a esa escena y vuelve a abrir allí la generación de la narración, de modo que el guion locutado siempre coincida con el texto.",
        },
        {
          id: "scene-sound-actions",
          title: "Trabajar con la narración de las escenas",
          videoUrl: null,
          text:
            "En **Sonidos de Escena**, despliega una escena para ver sus clips de narración. Los textos de narración largos se pueden desplegar con **Mostrar Texto**. Cada clip tiene estos botones:\n\n" +
            "- **Reproducir**: escucha el clip directamente en el panel.\n" +
            "- **Vídeo**: abre **Generar Video** con esa narración ya rellenada. Un indicador cuenta los trabajos de vídeo que siguen en curso.\n" +
            "- **Ver Escena**: salta a la escena en el lienzo.\n" +
            "- **Editar**: abre la ventana de sonido en **Voz de Narración AI** para esa escena, para que puedas regenerar la narración.\n" +
            "- **Eliminar**: quita el clip de la escena.\n\n" +
            "Para darle a una escena una narración nueva, usa **Añadir Sonido** debajo de esa escena.",
        },
        {
          id: "copy-text",
          title: "Copiar el texto",
          videoUrl: null,
          text:
            "**Resumen**, **Títulos del Documento** y **Textos de Narración** tienen cada uno un botón **Copiar Todo** en la parte superior que copia todo el panel al portapapeles. **Textos de Narración** copia el título de cada escena seguido de su narración.\n\n" +
            "Resulta útil para revisar el guion fuera del editor, compartirlo para su aprobación o reutilizarlo en otro documento.",
        },
      ],
    },
  },
};

export default knowledgeBaseGuide;
